<template>
  <v-container fluid class="chat-page">
    <div class="chat" :class="{ 'chat--thread': !!current }">
      <!-- conversations -->
      <aside class="chat__list">
        <div class="chat__list-head">Чат</div>
        <div v-if="listLoading && !conversations.length" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
        </div>
        <div v-else-if="!conversations.length" class="chat__list-empty">
          <template v-if="isPsychologist">
            Тут з'являться повідомлення від клієнтів. Клієнт може написати вам зі сторінки вашої анкети на сайті.
          </template>
          <template v-else>
            Тут будуть ваші розмови з психологами. Відкрийте анкету психолога на сайті й натисніть «Написати».
            <v-btn depressed outlined color="primary" class="mt-4" :href="siteUrl + '/therapists'">Каталог психологів</v-btn>
          </template>
        </div>
        <button
          v-for="item in conversations"
          :key="item.id"
          type="button"
          class="chat__item"
          :class="{ 'chat__item--active': current && current.id === item.id }"
          @click="openConversation(item)"
        >
          <span class="chat__avatar">
            <img v-if="item.other.photo" :src="item.other.photo" alt="">
            <span v-else>{{ initials(item.other.name) }}</span>
          </span>
          <span class="chat__item-main">
            <span class="chat__item-top">
              <span class="chat__item-name">{{ item.other.name }}</span>
              <span class="chat__item-time">{{ shortTime(item.last_message_at) }}</span>
            </span>
            <span class="chat__item-bottom">
              <span class="chat__item-last">{{ lastText(item) }}</span>
              <span v-if="item.unread" class="chat__badge">{{ item.unread }}</span>
            </span>
          </span>
        </button>
      </aside>

      <!-- messages -->
      <section class="chat__thread">
        <template v-if="current">
          <header class="chat__thread-head">
            <button type="button" class="chat__back" aria-label="Назад" @click="closeConversation">
              <v-icon>mdi-arrow-left</v-icon>
            </button>
            <span class="chat__avatar chat__avatar--sm">
              <img v-if="current.other.photo" :src="current.other.photo" alt="">
              <span v-else>{{ initials(current.other.name) }}</span>
            </span>
            <span class="chat__thread-title">
              <a v-if="current.other.public_url" :href="current.other.public_url" target="_blank" rel="noopener">{{ current.other.name }}</a>
              <template v-else>{{ current.other.name }}</template>
              <span v-if="current.other.specialty" class="chat__thread-sub">{{ current.other.specialty }}</span>
            </span>
            <v-btn v-if="isPsychologist" small depressed color="primary" class="chat__recommend" @click="openRecommend">
              <v-icon left small>mdi-clipboard-text-outline</v-icon>
              <span class="chat__recommend-text">Рекомендувати тест</span>
            </v-btn>
          </header>

          <div ref="scroller" class="chat__messages" @scroll="onScroll">
            <div v-if="hasMore" class="text-center my-2">
              <v-btn small text color="primary" :loading="olderLoading" @click="loadOlder">Попередні повідомлення</v-btn>
            </div>
            <div v-if="threadLoading" class="text-center py-8">
              <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
            </div>
            <p v-else-if="!messages.length" class="chat__hint">
              {{ isPsychologist ? "Повідомлень ще немає." : "Напишіть психологу: коротко про свій запит або про зручний час консультації." }}
            </p>
            <template v-for="(message, i) in messages">
              <div v-if="newDay(i)" :key="'d' + message.id" class="chat__day">{{ dayTitle(message.created_at) }}</div>
              <div :key="message.id" class="chat__row" :class="{ 'chat__row--mine': message.mine }">
                <div class="chat__bubble" :class="['chat__bubble--' + message.type, { 'chat__bubble--mine': message.mine }]">
                  <!-- a psychologist recommends a test -->
                  <template v-if="message.type === 'test'">
                    <div class="chat__card-label">Рекомендований тест</div>
                    <div class="chat__card-title">{{ message.payload.test_title }}</div>
                    <div v-if="message.body" class="chat__text">{{ message.body }}</div>
                    <div class="chat__card-actions">
                      <span v-if="message.payload.result_id" class="chat__done"><v-icon small color="#1C9A5B">mdi-check-circle</v-icon> Пройдено</span>
                      <template v-else-if="isPsychologist"><span class="chat__waiting">Очікує проходження</span></template>
                      <v-btn v-else small depressed color="primary" :href="testUrl(message)" target="_blank" rel="noopener">Пройти тест</v-btn>
                    </div>
                  </template>
                  <!-- the client's result of a recommended test -->
                  <template v-else-if="message.type === 'test_result'">
                    <div class="chat__card-label">Результат тесту</div>
                    <div class="chat__card-title">{{ message.payload.test_title }}</div>
                    <div v-for="(score, s) in message.payload.scores || []" :key="s" class="chat__score">
                      <span v-if="score.title" class="chat__score-scale">{{ score.title }}:</span>
                      <b>{{ score.score }}</b> з {{ score.max }}<template v-if="score.result_title"> · {{ score.result_title }}</template>
                    </div>
                    <div class="chat__card-actions">
                      <v-btn small depressed outlined color="primary" @click="showResult(message.payload.result_id)">Відповіді</v-btn>
                    </div>
                  </template>
                  <!-- a photo (e.g. a metaphoric card) or a document -->
                  <template v-else-if="message.type === 'file'">
                    <a v-if="message.payload.image" :href="message.payload.url" target="_blank" rel="noopener" class="chat__image">
                      <img :src="message.payload.url" :alt="message.payload.name" @load="onImageLoad">
                    </a>
                    <a v-else :href="message.payload.url" target="_blank" rel="noopener" class="chat__file">
                      <v-icon class="chat__file-icon">{{ fileIcon(message.payload.name) }}</v-icon>
                      <span class="chat__file-main">
                        <span class="chat__file-name">{{ message.payload.name }}</span>
                        <span class="chat__file-size">{{ fileSize(message.payload.size) }}</span>
                      </span>
                    </a>
                    <div v-if="message.body" class="chat__text" v-html="linkify(message.body)"></div>
                  </template>
                  <div v-else class="chat__text" v-html="linkify(message.body)"></div>
                  <div class="chat__meta">
                    {{ time(message.created_at) }}
                    <v-icon v-if="message.mine" x-small :color="message.id <= otherReadId ? '#BFF3F8' : 'rgba(255,255,255,.6)'">
                      {{ otherReadId >= message.id ? "mdi-check-all" : "mdi-check" }}
                    </v-icon>
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div v-if="attachment" class="chat__attachment">
            <img v-if="attachment.preview" :src="attachment.preview" alt="" class="chat__attachment-thumb">
            <v-icon v-else color="primary">{{ fileIcon(attachment.file.name) }}</v-icon>
            <span class="chat__attachment-name">{{ attachment.file.name }}</span>
            <span class="chat__file-size">{{ fileSize(attachment.file.size) }}</span>
            <button type="button" class="chat__attachment-remove" aria-label="Прибрати файл" @click="clearAttachment">
              <v-icon small>mdi-close</v-icon>
            </button>
          </div>
          <form class="chat__composer" @submit.prevent="send">
            <input ref="file" type="file" class="d-none" :accept="accept" @change="onFile">
            <button type="button" class="chat__attach" aria-label="Прикріпити фото або файл" title="Прикріпити фото або файл" @click="$refs.file.click()">
              <v-icon>mdi-paperclip</v-icon>
            </button>
            <textarea
              ref="input"
              v-model="draft"
              class="chat__input"
              rows="1"
              maxlength="4000"
              placeholder="Повідомлення…"
              @keydown.enter.exact.prevent="send"
              @input="autoGrow"
            ></textarea>
            <v-btn type="submit" fab small depressed color="primary" :loading="sending" :disabled="!draft.trim() && !attachment" aria-label="Надіслати">
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </form>
        </template>
        <div v-else class="chat__placeholder">
          <v-icon size="56" color="#B8D6DD">mdi-message-text-outline</v-icon>
          <p>Оберіть розмову зліва</p>
        </div>
      </section>
    </div>

    <!-- psychologist: recommend a professional test -->
    <v-dialog v-model="recommendDialog" max-width="520">
      <v-card class="pa-5">
        <p class="title mb-1">Рекомендувати тест</p>
        <p class="text-body-2 grey--text text--darken-1 mb-4">
          Клієнт отримає тест у чаті. Результат прийде сюди ж і збережеться в кабінеті клієнта.
        </p>
        <p v-if="testsLoaded && !tests.length" class="text-body-2">Професійних тестів на сайті ще немає.</p>
        <v-select
          v-else
          v-model="recommendTestId"
          :items="tests"
          item-text="title"
          item-value="id"
          label="Тест"
          outlined
          dense
        ></v-select>
        <v-textarea v-model="recommendComment" label="Коментар (необов'язково)" outlined rows="2" auto-grow counter="1000" maxlength="1000"></v-textarea>
        <div class="d-flex justify-end" style="gap: 8px">
          <v-btn text @click="recommendDialog = false">Скасувати</v-btn>
          <v-btn depressed color="primary" :disabled="!recommendTestId" :loading="recommending" @click="recommend">Надіслати</v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- answers of a test result -->
    <v-dialog v-model="resultDialog" max-width="640" scrollable>
      <v-card>
        <v-card-title class="d-flex">
          <span class="chat__result-title">{{ result ? result.test.title : "Відповіді" }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="resultDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="!result" class="text-center py-6"><v-progress-circular indeterminate color="primary"></v-progress-circular></div>
          <template v-else>
            <div v-for="(score, s) in result.scores" :key="'s' + s" class="chat__result-score">
              <b>{{ score.title ? score.title + ": " : "" }}{{ score.score }} з {{ score.max }}</b>
              <span v-if="score.result_title"> · {{ score.result_title }}</span>
              <div v-if="score.result_text" class="chat__result-text">{{ score.result_text }}</div>
            </div>
            <ol class="chat__answers">
              <li v-for="(item, a) in result.answers" :key="a">
                <div>{{ item.question }}</div>
                <div class="chat__answer">{{ item.answer }}</div>
              </li>
            </ol>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Vue from "vue";
import ApiClient from "@/libs/http-client";

const POLL_MS = 4000;
// same limits as the API: photos are shown in the chat, documents are downloaded
const FILE_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif", "heic", "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "odt", "rtf", "txt"];
const FILE_MAX_BYTES = 10 * 1024 * 1024;
// big phone photos are scaled down before upload
const IMAGE_MAX_SIDE = 2000;

function escapeHtml(text) {
  return String(text).replace(/[&<>"']/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char]));
}

function shrinkImage(file) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type) || file.size < 1024 * 1024) return Promise.resolve(file);
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, IMAGE_MAX_SIDE / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (!blob || blob.size >= file.size) return resolve(file);
        resolve(new File([blob], file.name.replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" }));
      }, "image/jpeg", 0.85);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(file);
    };
    img.src = url;
  });
}
const LIST_MS = 15000;

function toast(text, icon = "success") {
  Vue.swal({
    target: document.getElementById("main"),
    text,
    icon,
    timer: 3500,
    toast: true,
    position: "top-right",
    showConfirmButton: false,
  });
}

export default {
  name: "Chat",
  metaInfo() {
    return { title: "Платформа / Чат" };
  },
  data() {
    return {
      siteUrl: (process.env.VUE_APP_MAIN_PAGE || "").replace(/\/$/, ""),
      conversations: [],
      listLoading: true,
      current: null,
      messages: [],
      threadLoading: false,
      olderLoading: false,
      hasMore: false,
      otherReadId: 0,
      draft: "",
      attachment: null,
      accept: FILE_EXTENSIONS.map((ext) => "." + ext).join(","),
      sending: false,
      pollTimer: null,
      listTimer: null,
      polling: false,
      stickToBottom: true,
      recommendDialog: false,
      tests: [],
      testsLoaded: false,
      recommendTestId: null,
      recommendComment: "",
      recommending: false,
      resultDialog: false,
      result: null,
    };
  },
  computed: {
    isPsychologist() {
      return this.$store.getters["user/getUser"].role === "psychologist";
    },
    lastId() {
      return this.messages.length ? this.messages[this.messages.length - 1].id : 0;
    },
  },
  mounted() {
    this.start();
    this.listTimer = setInterval(() => {
      if (!document.hidden) this.loadList();
    }, LIST_MS);
  },
  beforeDestroy() {
    clearInterval(this.listTimer);
    clearTimeout(this.pollTimer);
  },
  methods: {
    start() {
      const to = this.$route.query.to;
      const wanted = this.$route.query.c;
      // a client came from a psychologist's page on the site: open (or create) that conversation
      const opening = to && !this.isPsychologist
        ? ApiClient.post("api/chat/conversations", { profile_id: to }).then((response) => response.data.data).catch(() => null)
        : Promise.resolve(null);
      Promise.all([this.loadList(), opening]).then(([, started]) => {
        if (to || wanted) this.$router.replace({ query: {} }).catch(() => {});
        const target = started || this.conversations.find((item) => item.id === wanted);
        if (started && !this.conversations.find((item) => item.id === started.id)) {
          this.conversations.unshift(started);
        }
        if (target) this.openConversation(this.conversations.find((item) => item.id === target.id) || target);
        else if (window.innerWidth > 800 && this.conversations.length) this.openConversation(this.conversations[0]);
      });
    },
    loadList() {
      return ApiClient.get("api/chat/conversations")
        .then((response) => {
          const list = response.data.data || [];
          // a just-started conversation without messages stays in the list
          if (this.current && !list.find((item) => item.id === this.current.id)) list.unshift(this.current);
          this.conversations = list;
        })
        .catch(() => {})
        .finally(() => {
          this.listLoading = false;
        });
    },
    openConversation(item) {
      if (this.current && this.current.id === item.id) return;
      clearTimeout(this.pollTimer);
      this.current = item;
      this.messages = [];
      this.hasMore = false;
      this.draft = "";
      this.clearAttachment();
      this.threadLoading = true;
      this.stickToBottom = true;
      ApiClient.get(`api/chat/conversations/${item.id}/messages`)
        .then((response) => {
          if (!this.current || this.current.id !== item.id) return;
          this.messages = response.data.data;
          this.hasMore = response.data.meta.has_more;
          this.otherReadId = response.data.meta.other_read_id || 0;
          item.unread = 0;
          this.$root.$emit("menu-counters-refresh");
          this.scrollDown();
          this.schedulePoll();
        })
        .catch((error) => this.onError(error))
        .finally(() => {
          this.threadLoading = false;
        });
    },
    closeConversation() {
      clearTimeout(this.pollTimer);
      this.current = null;
      this.messages = [];
    },
    schedulePoll() {
      clearTimeout(this.pollTimer);
      this.pollTimer = setTimeout(this.poll, POLL_MS);
    },
    poll() {
      if (!this.current) return;
      if (document.hidden || this.polling) {
        this.schedulePoll();
        return;
      }
      const id = this.current.id;
      this.polling = true;
      ApiClient.get(`api/chat/conversations/${id}/messages?after=${this.lastId}`)
        .then((response) => {
          if (!this.current || this.current.id !== id) return;
          this.otherReadId = response.data.meta.other_read_id || 0;
          this.append(response.data.data);
        })
        .catch(() => {})
        .finally(() => {
          this.polling = false;
          this.schedulePoll();
        });
    },
    append(items) {
      const known = new Set(this.messages.map((item) => item.id));
      const fresh = items.filter((item) => !known.has(item.id));
      if (!fresh.length) return;
      this.messages = this.messages.concat(fresh);
      const last = fresh[fresh.length - 1];
      if (this.current) {
        this.current.last_message_at = last.created_at;
        const payload = last.payload || {};
        this.current.last_message = {
          type: last.type,
          body: last.type === "text" ? last.body : last.type === "file" ? payload.name : payload.test_title,
          image: last.type === "file" && !!payload.image,
          mine: last.mine,
        };
      }
      if (this.stickToBottom) this.scrollDown();
    },
    loadOlder() {
      if (!this.messages.length) return;
      const scroller = this.$refs.scroller;
      const before = scroller ? scroller.scrollHeight : 0;
      this.olderLoading = true;
      ApiClient.get(`api/chat/conversations/${this.current.id}/messages?before=${this.messages[0].id}`)
        .then((response) => {
          this.messages = response.data.data.concat(this.messages);
          this.hasMore = response.data.meta.has_more;
          this.$nextTick(() => {
            if (scroller) scroller.scrollTop = scroller.scrollHeight - before;
          });
        })
        .finally(() => {
          this.olderLoading = false;
        });
    },
    send() {
      const body = this.draft.trim();
      if ((!body && !this.attachment) || this.sending || !this.current) return;
      this.sending = true;
      let request;
      if (this.attachment) {
        const form = new FormData();
        form.append("file", this.attachment.file);
        if (body) form.append("body", body);
        request = ApiClient.post(`api/chat/conversations/${this.current.id}/files`, form);
      } else {
        request = ApiClient.post(`api/chat/conversations/${this.current.id}/messages`, { body });
      }
      request
        .then((response) => {
          this.draft = "";
          this.clearAttachment();
          this.$nextTick(this.autoGrow);
          this.stickToBottom = true;
          this.append([response.data.data]);
          this.loadList();
        })
        .catch((error) => {
          this.onError(error);
          const errors = error && error.response && error.response.data && error.response.data.errors;
          if (errors && errors.file) toast(errors.file[0], "error");
          else if (error && error.response && error.response.status === 413) toast("Файл завеликий: до 10 МБ", "error");
        })
        .finally(() => {
          this.sending = false;
          this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
        });
    },
    onFile(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (!FILE_EXTENSIONS.includes(ext)) {
        toast("Можна надіслати фото, PDF, Word, Excel, PowerPoint або текстовий файл", "error");
        return;
      }
      shrinkImage(file).then((ready) => {
        if (ready.size > FILE_MAX_BYTES) {
          toast("Файл завеликий: до 10 МБ", "error");
          return;
        }
        this.clearAttachment();
        const preview = /^image\/(jpeg|png|webp|gif)$/.test(ready.type) ? URL.createObjectURL(ready) : null;
        this.attachment = { file: ready, preview };
        this.$nextTick(() => this.$refs.input && this.$refs.input.focus());
      });
    },
    clearAttachment() {
      if (this.attachment && this.attachment.preview) URL.revokeObjectURL(this.attachment.preview);
      this.attachment = null;
    },
    onImageLoad() {
      if (this.stickToBottom) this.scrollDown();
    },
    // links in messages (e.g. the video room) open in a new tab; the rest of the text stays plain
    linkify(text) {
      return escapeHtml(text || "").replace(/(https?:\/\/|www\.)[^\s<]+/gi, (match) => {
        const tail = (match.match(/[.,;:!?)»]+$/) || [""])[0];
        const link = tail ? match.slice(0, -tail.length) : match;
        const href = /^www\./i.test(link) ? "https://" + link : link;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer">${link}</a>${tail}`;
      });
    },
    fileIcon(name) {
      const ext = (String(name || "").split(".").pop() || "").toLowerCase();
      if (ext === "pdf") return "mdi-file-pdf-box";
      if (["doc", "docx", "odt", "rtf"].includes(ext)) return "mdi-file-word-box";
      if (["xls", "xlsx"].includes(ext)) return "mdi-file-excel-box";
      if (["ppt", "pptx"].includes(ext)) return "mdi-file-powerpoint-box";
      if (["jpg", "jpeg", "png", "webp", "gif", "heic"].includes(ext)) return "mdi-file-image";
      return "mdi-file-document-outline";
    },
    fileSize(bytes) {
      if (!bytes) return "";
      if (bytes < 1024 * 1024) return Math.max(1, Math.round(bytes / 1024)) + " КБ";
      return (bytes / 1024 / 1024).toFixed(1).replace(".", ",") + " МБ";
    },
    openRecommend() {
      this.recommendTestId = null;
      this.recommendComment = "";
      this.recommendDialog = true;
      if (!this.testsLoaded) {
        ApiClient.get("api/chat/tests").then((response) => {
          this.tests = response.data.data;
          this.testsLoaded = true;
        });
      }
    },
    recommend() {
      this.recommending = true;
      ApiClient.post(`api/chat/conversations/${this.current.id}/recommend-test`, {
        test_id: this.recommendTestId,
        comment: this.recommendComment.trim() || null,
      })
        .then((response) => {
          this.recommendDialog = false;
          this.stickToBottom = true;
          this.append([response.data.data]);
          toast("Тест надіслано клієнту");
        })
        .catch((error) => this.onError(error))
        .finally(() => {
          this.recommending = false;
        });
    },
    showResult(id) {
      this.result = null;
      this.resultDialog = true;
      ApiClient.get(`api/chat/test-results/${id}`).then((response) => {
        this.result = response.data.data;
      });
    },
    onError(error) {
      if (error && error.response && error.response.status === 409 && this.$router.app) {
        this.$router.app.$emit("subscription-locked");
      }
    },
    testUrl(message) {
      return `${this.siteUrl}/tests/${message.payload.test_slug}?rec=${message.id}`;
    },
    onScroll() {
      const scroller = this.$refs.scroller;
      if (!scroller) return;
      this.stickToBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < 80;
    },
    scrollDown() {
      this.$nextTick(() => {
        const scroller = this.$refs.scroller;
        if (scroller) scroller.scrollTop = scroller.scrollHeight;
      });
    },
    autoGrow() {
      const input = this.$refs.input;
      if (!input) return;
      input.style.height = "auto";
      input.style.height = Math.min(input.scrollHeight, 160) + "px";
    },
    initials(name) {
      return String(name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join("");
    },
    lastText(item) {
      const last = item.last_message;
      if (!last) return "";
      const prefix = last.mine ? "Ви: " : "";
      if (last.type === "test") return prefix + "Тест: " + last.body;
      if (last.type === "test_result") return prefix + "Результат тесту: " + last.body;
      if (last.type === "file") return prefix + (last.image ? "Фото" : "Файл: " + last.body);
      return prefix + last.body;
    },
    time(value) {
      return new Date(value).toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" });
    },
    shortTime(value) {
      if (!value) return "";
      const date = new Date(value);
      const today = new Date();
      if (date.toDateString() === today.toDateString()) return this.time(value);
      return date.toLocaleDateString("uk-UA", { day: "numeric", month: "short" });
    },
    newDay(i) {
      if (i === 0) return true;
      return new Date(this.messages[i - 1].created_at).toDateString() !== new Date(this.messages[i].created_at).toDateString();
    },
    dayTitle(value) {
      const date = new Date(value);
      const today = new Date();
      const yesterday = new Date(Date.now() - 86400000);
      if (date.toDateString() === today.toDateString()) return "Сьогодні";
      if (date.toDateString() === yesterday.toDateString()) return "Вчора";
      return date.toLocaleDateString("uk-UA", { day: "numeric", month: "long", year: date.getFullYear() === today.getFullYear() ? undefined : "numeric" });
    },
  },
};
</script>

<style lang="scss" scoped>
$teal: #00A3B7;
$line: #D9E4EB;
$text: #575756;

.chat-page {
  max-width: 1200px;
  padding-top: 28px;
  padding-bottom: 40px;
}
.chat {
  display: flex;
  height: calc(100vh - 170px);
  min-height: 480px;
  overflow: hidden;
  border: 1px solid $line;
  border-radius: 16px;
  background: #fff;
}
.chat__list {
  flex: 0 0 320px;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  border-right: 1px solid $line;
}
.chat__list-head {
  padding: 18px 20px 12px;
  color: $teal;
  font-size: 22px;
  font-weight: 500;
}
.chat__list-empty {
  padding: 12px 20px;
  color: #868686;
  font-size: 15px;
  line-height: 1.5;
}
.chat__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 16px;
  text-align: left;
  transition: background 0.2s;
  &:hover {
    background: #F4F8FA;
  }
  &--active {
    background: #E6F6F8;
  }
}
.chat__avatar {
  flex: 0 0 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  overflow: hidden;
  border-radius: 50%;
  background: #E6F6F8;
  color: $teal;
  font-weight: 500;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 25%;
  }
  &--sm {
    flex-basis: 38px;
    width: 38px;
    height: 38px;
    font-size: 14px;
  }
}
.chat__item-main {
  flex: 1 1 auto;
  min-width: 0;
}
.chat__item-top,
.chat__item-bottom {
  display: flex;
  align-items: center;
  gap: 8px;
}
.chat__item-name {
  flex: 1 1 auto;
  overflow: hidden;
  color: $text;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chat__item-time {
  flex: 0 0 auto;
  color: #9A9A9A;
  font-size: 12px;
}
.chat__item-last {
  flex: 1 1 auto;
  overflow: hidden;
  color: #868686;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chat__badge {
  flex: 0 0 auto;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #E5484D;
  color: #fff;
  font-size: 12px;
  line-height: 20px;
  text-align: center;
}
.chat__thread {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: #F7FAFB;
}
.chat__thread-head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 18px;
  border-bottom: 1px solid $line;
  background: #fff;
}
.chat__back {
  display: none;
}
.chat__thread-title {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  color: $text;
  font-weight: 500;
  a {
    color: $teal;
    text-decoration: none;
  }
}
.chat__thread-sub {
  color: #868686;
  font-size: 13px;
  font-weight: 400;
}
.chat__messages {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px 20px;
}
.chat__hint {
  margin: 24px auto;
  max-width: 420px;
  color: #868686;
  text-align: center;
}
.chat__day {
  margin: 14px 0 10px;
  color: #9A9A9A;
  font-size: 13px;
  text-align: center;
}
.chat__row {
  display: flex;
  margin-bottom: 8px;
  &--mine {
    justify-content: flex-end;
  }
}
.chat__bubble {
  max-width: min(78%, 560px);
  padding: 9px 14px 6px;
  border-radius: 16px 16px 16px 4px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  color: $text;
  &--mine {
    border-radius: 16px 16px 4px 16px;
    background: $teal;
    color: #fff;
  }
  &--test,
  &--test_result {
    min-width: 240px;
    border: 1px solid #BFE6EC;
    background: #fff;
    color: $text;
  }
}
.chat__text {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  font-size: 15px;
  line-height: 1.45;
}
.chat__text ::v-deep a {
  color: $teal;
  text-decoration: underline;
  overflow-wrap: anywhere;
}
.chat__bubble--mine .chat__text ::v-deep a {
  color: #fff;
}
.chat__bubble--file {
  padding: 6px 6px 4px;
  .chat__text {
    padding: 6px 8px 0;
  }
  .chat__meta {
    padding: 0 8px;
  }
}
.chat__image {
  display: block;
  img {
    display: block;
    max-width: 100%;
    max-height: 320px;
    border-radius: 12px;
  }
}
.chat__file {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 220px;
  max-width: 100%;
  padding: 8px 10px;
  border-radius: 12px;
  background: rgba(0, 163, 183, 0.08);
  color: inherit !important;
  text-decoration: none;
}
.chat__bubble--mine .chat__file {
  background: rgba(255, 255, 255, 0.18);
}
.chat__file-icon {
  color: inherit !important;
  font-size: 34px !important;
}
.chat__file-main {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.chat__file-name {
  overflow: hidden;
  font-size: 14px;
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chat__file-size {
  flex: 0 0 auto;
  font-size: 12px;
  opacity: 0.75;
}
.chat__attachment {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  border-top: 1px solid $line;
  background: #F4F8FA;
  color: $text;
  font-size: 14px;
}
.chat__attachment-thumb {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
}
.chat__attachment-name {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chat__attachment-remove {
  margin-left: auto;
  padding: 4px;
  border-radius: 50%;
  &:hover {
    background: #E6F6F8;
  }
}
.chat__attach {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: #868686;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: #E6F6F8;
    color: $teal;
  }
  .v-icon {
    color: inherit;
  }
}
.chat__meta {
  margin-top: 2px;
  font-size: 11px;
  text-align: right;
  opacity: 0.75;
}
.chat__bubble--test .chat__meta,
.chat__bubble--test_result .chat__meta {
  color: #868686;
  .v-icon {
    color: $teal !important;
  }
}
.chat__card-label {
  color: $teal;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}
.chat__card-title {
  margin: 2px 0 6px;
  font-size: 16px;
  font-weight: 500;
}
.chat__card-actions {
  margin: 10px 0 4px;
}
.chat__done {
  color: #1C9A5B;
  font-size: 14px;
}
.chat__waiting {
  color: #C77700;
  font-size: 14px;
}
.chat__score {
  font-size: 15px;
}
.chat__score-scale {
  color: #868686;
}
.chat__composer {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid $line;
  background: #fff;
}
.chat__input {
  flex: 1 1 auto;
  max-height: 160px;
  padding: 10px 14px;
  border: 1px solid $line;
  border-radius: 20px;
  outline: none;
  resize: none;
  font-size: 15px;
  line-height: 1.4;
  &:focus {
    border-color: $teal;
  }
}
.chat__placeholder {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9A9A9A;
}
.chat__result-title {
  word-break: normal;
}
.chat__result-score {
  margin-bottom: 10px;
  color: $text;
  font-size: 16px;
}
.chat__result-text {
  margin-top: 4px;
  font-size: 14px;
}
.chat__answers {
  margin-top: 16px;
  padding-left: 20px !important;
  color: $text;
  li {
    margin-bottom: 10px;
  }
}
.chat__answer {
  color: $teal;
  font-weight: 500;
}

@media (max-width: 800px) {
  .chat-page {
    padding: 12px 8px 20px;
  }
  .chat {
    height: calc(100vh - 110px);
    &:not(.chat--thread) {
      height: auto;
      min-height: 320px;
    }
  }
  .chat__list {
    flex-basis: 100%;
    border-right: 0;
  }
  .chat__thread {
    display: none;
  }
  .chat--thread {
    .chat__list {
      display: none;
    }
    .chat__thread {
      display: flex;
    }
  }
  .chat__back {
    display: flex;
  }
  .chat__recommend-text {
    display: none;
  }
  .chat__recommend ::v-deep .v-icon--left {
    margin: 0;
  }
  .chat__messages {
    padding: 12px;
  }
  .chat__bubble {
    max-width: 88%;
  }
}
</style>
